import "../telemetry";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import {
  MESSAGE_TYPES,
  type TrackEventPayload,
  type TrackErrorPayload,
} from "../shared/messaging";
import {
  trackWidgetEvent,
  trackError,
  isWidgetEventName,
} from "../telemetry/events";
import { getParentOrigin } from "./utils/getParentOrigin";

window.addEventListener("message", (event: MessageEvent) => {
  const { data } = event;
  if (!data || typeof data !== "object") return;

  // Validate the origin of postMessage events to avoid fake telemetry
  const expectedOrigin = getParentOrigin();
  if (!expectedOrigin || event.origin !== expectedOrigin) {
    console.warn(
      "[Widget] Ignoring telemetry message from unexpected origin:",
      event.origin,
    );
    return;
  }

  switch (data.type) {
    case MESSAGE_TYPES.TRACK_EVENT: {
      const payload = data.payload as TrackEventPayload;

      // Ensure payload has required fields before using it.
      // Malicious or buggy code could send invalid payloads that crash the widget.
      if (!payload || typeof payload !== "object") {
        console.warn("[Widget] Invalid TRACK_EVENT payload: not an object");
        return;
      }

      if (!payload.eventName || typeof payload.eventName !== "string") {
        console.warn(
          "[Widget] Invalid TRACK_EVENT payload: missing or invalid eventName",
        );
        return;
      }

      if (payload.attributes && typeof payload.attributes !== "object") {
        console.warn(
          "[Widget] Invalid TRACK_EVENT payload: attributes must be an object",
        );
        return;
      }

      if (!isWidgetEventName(payload.eventName)) {
        console.warn("[Widget] Unknown event name:", payload.eventName);
        return;
      }

      // TypeScript now knows payload.eventName is a valid WidgetEventName
      trackWidgetEvent(payload.eventName, payload.attributes || {});
      break;
    }
    case MESSAGE_TYPES.TRACK_ERROR: {
      const payload = data.payload as TrackErrorPayload;

      if (!payload || typeof payload !== "object") {
        console.warn("[Widget] Invalid TRACK_ERROR payload: not an object");
        return;
      }

      if (!payload.error?.message) {
        console.warn(
          "[Widget] Invalid TRACK_ERROR payload: missing error.message",
        );
        return;
      }

      const error = new Error(payload.error.message);
      if (payload.error.stack) {
        error.stack = payload.error.stack;
      }
      trackError(error, payload.attributes || {});
      break;
    }
  }
});

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
